/**
 * 1.3.1 Administration → Media portal → Companies & users → a company → Activity (Q1): sign-ins, uploads, decisions
 * and comments, newest first, filtered by PORTAL_ACTIVITY_KINDS, a page at a time with Show more (nextCursor), and
 * Download CSV (?format=csv). Rendered by PortalCompanies (Companies.tsx) for the chosen company. Q1's file.
 */
import { useState } from 'react';
import { Download, History } from 'lucide-react';
import type { PortalActivityKind, PortalActivityRowDTO, PortalCompanyDTO } from '@prism/contracts';
import { usePortalCompanyActivity } from '../../../api/hooks/portalAdmin';
import { TableScroll } from '../../../components/TableScroll';
import { relative } from '../../../lib/format';

const KIND_LABEL: Record<PortalActivityKind, string> = { all: 'Everything', sign_ins: 'Sign-ins', uploads: 'Uploads', decisions: 'Decisions', comments: 'Comments' };

function outcomePill(r: PortalActivityRowDTO) {
  if (r.outcome === 'success') return null;
  return <span className={`pill ${r.outcome === 'denied' ? 'warning' : 'danger'}`}>{r.outcome === 'denied' ? 'Refused' : 'Failed'}</span>;
}

export function CompanyActivity({ company }: { company: PortalCompanyDTO }) {
  const [kind, setKind] = useState<PortalActivityKind>('all');
  const q = usePortalCompanyActivity(company.id, kind);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const csv = `/api/v1/admin/portal/companies/${company.id}/activity?format=csv&kind=${kind}`;
  return (
    <section className="panel-card portal-section" data-testid="portal-company-activity">
      <div className="portal-card-head">
        <h3><History size={16} color="var(--accent-strong)" /> Activity of {company.name}</h3>
        <div className="pa-tools">
          <label className="sr-only" htmlFor="pa-kind">Show</label>
          <select id="pa-kind" className="select" value={kind} onChange={(e) => setKind(e.target.value as PortalActivityKind)} data-testid="portal-activity-kind">
            {(Object.keys(KIND_LABEL) as PortalActivityKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
          <a className="btn sm" href={csv} download data-testid="portal-activity-csv"><Download size={14} /> Download CSV</a>
        </div>
      </div>
      <p className="muted portal-lede">What the company’s users did on the portal, and what PRISM’s reviewers decided about their uploads, newest first. Kept with the audit trail.</p>
      <TableScroll>
        <table className="table compact">
          <thead><tr><th>When</th><th>Who</th><th>What</th><th /></tr></thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={4} className="muted">Loading…</td></tr>}
            {q.isError && <tr><td colSpan={4} className="portal-bad">The activity could not be loaded.</td></tr>}
            {q.data && !items.length && <tr><td colSpan={4} className="muted">Nothing yet{kind === 'all' ? '' : ' of this kind'}.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} data-testid="portal-activity-row" data-action={r.action}>
                <td className="nowrap muted" title={new Date(r.at).toLocaleString()}>{relative(r.at)}</td>
                <td className="wrap pa-who">
                  {r.actor ?? '—'}
                  <div className="faint">{r.actorKind === 'portal' ? 'Supplier' : 'PRISM'}</div>
                </td>
                <td className="wrap pa-what">{r.summary}</td>
                <td className="nowrap">{outcomePill(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      {q.hasNextPage && (
        <div className="portal-save-row">
          <button className="btn" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()} data-testid="portal-activity-more">{q.isFetchingNextPage ? 'Loading…' : 'Show more'}</button>
        </div>
      )}
    </section>
  );
}
